import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useProjectChat } from "@services/ProjectChatContext";
import { useSidebar } from "@services/SidebarContext";
import { useConversation } from "@pages/Assistant/utils/useConversation";
import Markdown from "markdown-to-jsx";
import { overrides } from "@shared/components/Assistant/MarkdownConfig";
import ThumbButtons from "@shared/components/Feedback/ThumbButtons";
import FeedbackSubmission from "@shared/components/Feedback/FeedbackSubmission";
import SubTitleHeader from "@shared/components/SubTitleHeader";
import Loader from "@shared/components/Loader";
import AssistantForm, { Option } from "./components/AssistantForm";
import Header from "./components/Header";
import { useChatSessionManager } from "./utils/useChatSessionManager";
import { useCompanyName } from "./utils/useCompanyName";
import { useLoadingStream } from "./utils/useLoadingStream";
import {
  ThinkingPanel,
  StepsPanel,
  IntermediatePanel,
} from "./components/renderLoadingItems";
import { externalApiService } from "@services/ExternalApiService";
import { CitationViewItem } from "./utils/citations";
import { useResetAssistant } from "./utils/useResetAssistant";
import { useConversationFetcher } from "./utils/useConversationFetcher";
import { useAssistantLoaderState } from "./utils/useAssistantLoaderState";
import { useChatHistoryManager } from "./utils/useChatHistoryManager";
import { useFeedback } from "@shared/components/Feedback/utils/useFeedback";
import { convertS3UrlToHttps } from "./utils/utils";
import NewItemButton from "@shared/components/NewItemButton";
import TruncatedNameWithTooltip from "@shared/components/TruncatedNameWithTooltip";
import ReadOnlySelectedFilesView from "./components/ReadOnlySelectedFilesView";
import SourcesDropdown, { SourceOption } from "./components/SourcesDropdown";
import { useExternalApiToggles } from "./utils/useExternalApiToggles";
import ImageViewer from "./components/ImageViewer";
import { useRenderHelper } from "@pages/Assistant/utils/useRenderHelper";
import { useAssistantSocket } from "./utils/useAssistantSocket";

import {
  conversationFilesService,
  UploadedConversationFile,
} from "@services/ConversationFilesService";
import { getFileIcon } from "@pages/Project/components/Tabs/FileUploadTab";
import { toastService } from "@services/ToastService";

// --------------------- Helpers for per-message image chips ---------------------

type ApiAttachedFile = {
  id: string;
  name: string;
  status?: "processing" | "ready" | string;
};

/**
 * Returns only the newly added *file* attachments for the message at `index`
 * by diffing against the previous message.
 */
function getNewFileAttachmentsForMessage(
  chatLog: unknown[],
  index: number
): ApiAttachedFile[] {
  const current = (chatLog[index] as any)?.attached_files as
    | ApiAttachedFile[]
    | undefined;
  if (!current?.length) return [];

  const prev = (chatLog[index - 1] as any)?.attached_files as
    | ApiAttachedFile[]
    | undefined;
  const prevIds = new Set((prev || []).map((f) => f.id));

  return current.filter((f) => !prevIds.has(f.id));
}

// --------------------------------------------------------------------------------------------

interface AssistantPageProps {
  setShowDocxModal?: (show: boolean) => void;
  setDocxContent?: (content: string) => void;
  setShowExcelModal?: (show: boolean) => void;
  setExcelData?: (data: any[][]) => void;
}

type InlineBadge = {
  n: number;
  project_id?: string | null;
  file_id?: string;
  original_file?: string;
  page_number?: number;
  sheet_name?: string;
  start_row?: number;
  hoverText: string;
  kind?: "document" | "tabular" | "web";
  url?: string;
  title?: string;
};

const Assistant: React.FC<AssistantPageProps> = ({
  setShowDocxModal = () => false,
  setDocxContent = () => false,
  setShowExcelModal = () => false,
  setExcelData = () => false,
}) => {
  const [question, setQuestion] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [socket, setSocket] = useState<WebSocket | null>(null);
  const [citations, setCitations] = useState<any[]>([]);
  const [chatScope, setChatScope] = useState<string>("");
  const [selectedProject, setSelectedProject] = useState<Option>({} as Option);
  const [selectedReport, setSelectedReport] = useState<Option>({} as Option);
  const [currentUserQuestion, setCurrentUserQuestion] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [fetchedChatCount, setFetchedChatCount] = useState(0);
  const inlineBadgesRef = useRef<InlineBadge[]>([]);
  const nextBadgeNumRef = useRef<number>(1);

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { conversationId: conversationIdParam } = useParams();
  const [showLoading, setShowLoading] = useState<boolean>(false);

  const imageRef = useRef<any[]>([]); // holds streamed images

  const [showImageModal, setShowImageModal] = useState(false);
  const [modalImageSrc, setModalImageSrc] = useState<string | null>(null);
  const [modalImageAlt, setModalImageAlt] = useState<string>("");

  const { companyName } = useCompanyName();
  const { refreshChats, setSidebarDisabled, sidebarDisabled } = useSidebar();

  const thinkingReceived = useRef<boolean>(false);
  const citationsV2Ref = useRef<CitationViewItem[]>([]);

  // New: state for source selection (badge reflects enabled count)
  const [enabledSources, setEnabledSources] = useState<Set<string>>(new Set());

  const [sourceOptions, setSourceOptions] = useState<SourceOption[]>([]);

  // Uploaded & pending files state
  const [uploadedFiles, setUploadedFiles] = useState<
    UploadedConversationFile[]
  >([]);
  const [pendingFiles, setPendingFiles] = useState<UploadedConversationFile[]>(
    []
  );

  const headerUploadRef = React.useRef<HTMLInputElement | null>(null);
  const headerUploadId = React.useId();

  const [uploading, setUploading] = useState(false);
  const [deletingFileId, setDeletingFileId] = useState<string | null>(null);

  const {
    thinking: thinkingItem,
    steps: stepItem,
    ingest,
    clear: clearLoading,
  } = useLoadingStream();

  const {
    addMessageToChat,
    chatLog,
    setChatLog,
    setSelectedFiles,
    selectedFiles,
    clearChat,
  } = useProjectChat();

  const {
    isChatLoading,
    setIsChatLoading,
    loadingStepData,
    setLoadingStepData,
  } = useAssistantLoaderState();

  const {
    chatSessionId,
    setChatSessionId,
    chatSessionIdRef,
    conversationId,
    setConversationId,
    conversationIdRef,
    resetSession,
  } = useChatSessionManager(conversationIdParam);

  const { getConversationById } = useConversation();

  const { chatHistory, setChatHistory, botMessageCount, setBotMessageCount } =
    useChatHistoryManager({
      setConversationId,
      conversationId,
      conversationIdRef,
      conversationIdParam,
      setSearchParams,
      refreshChats,
    });

  useEffect(() => {
    return () => {
      if (socket) socket.close();
    };
  }, []);

  const {
    feedbackText,
    setFeedbackText,
    feedbackOption,
    setFeedbackOption,
    feedbackState,
    setFeedbackState,
    feedbackSubmittedFor,
    selectedThumbs,
    feedbackHiddenFor,
    feedbackUpdating,
    handleThumbClick,
    handleFeedbackSubmit,
    scrollToBottom,
    feedbackForIndex,
    chatLogRef,
    resetFeedbackState,
  } = useFeedback(chatSessionId || "");

  const showAndSetModalImage = (src: string, alt: string) => {
    setModalImageSrc(src || null);
    setModalImageAlt(alt);
    setShowImageModal(true);
  };

  const { fetchConversation } = useConversationFetcher({
    getConversationById,
    setChatLog,
    setChatHistory,
    setSelectedProject,
    setSelectedReport,
    setSelectedFiles,
    setSidebarDisabled,
    setLoading,
    setFetchedChatCount,
    setChatSessionId,
    setConversationIdRef: (val) => (conversationIdRef.current = val),
    setDocxContent,
    setExcelData,
    setShowDocxModal,
    setShowExcelModal,
    setEnabledSources,
    showAndSetModalImage,
  });

  const { resetChat } = useResetAssistant({
    resetSession,
    resetFeedbackState,
    setSelectedFiles,
    setCitations,
    setChatHistory,
    setQuestion,
    clearChat,
    setError,
    setLoadingStepData,
    setSocket,
    setSelectedProject,
    setSelectedReport,
    socket,
    setIsChatLoading,
    setLoading,
    setConversationId,
    conversationIdParam,
    fetchConversation,
    setEnabledSources,
    setUploadedFiles,
    setPendingFiles,
  });

  // ----- Render helper (inline images + citations) -----
  const { renderBotMessage } = useRenderHelper(
    overrides,
    inlineBadgesRef,
    setShowDocxModal,
    setDocxContent,
    setExcelData,
    setShowExcelModal,
    addMessageToChat,
    setChatLog,
    imageRef,
    setModalImageSrc,
    setModalImageAlt,
    setShowImageModal
  );

  const attachedFilesForMessage = React.useMemo(
    () =>
      (pendingFiles || []).map((f) => ({
        id: String(f.id),
        name: f.filename, // backend expects "name"
      })),
    [pendingFiles]
  );

  const { handleSubmit, tryAgain, scopedDefined } = useAssistantSocket(
    citationsV2Ref,
    inlineBadgesRef,
    nextBadgeNumRef,
    selectedProject,
    setError,
    selectedFiles,
    enabledSources,
    chatSessionId,
    conversationIdRef,
    selectedReport,
    companyName,
    setChatSessionId,
    chatSessionIdRef,
    setConversationId,
    conversationIdParam,
    setSearchParams,
    setIsChatLoading,
    setShowLoading,
    thinkingReceived,
    ingest,
    loadingStepData,
    setLoadingStepData,
    renderBotMessage,
    imageRef,
    setSidebarDisabled,
    question,
    clearLoading,
    addMessageToChat,
    setBotMessageCount,
    refreshChats,
    setChatHistory,
    setSocket,
    scrollToBottom,
    socket,
    chatHistory,
    setChatLog,
    setShowDocxModal,
    setDocxContent,
    setExcelData,
    setShowExcelModal,
    showAndSetModalImage,
    setQuestion,
    chatLog,
    setCurrentUserQuestion,
    conversationId,
    attachedFilesForMessage
  );

  const handleSubmitWrapped = async (e: React.FormEvent<HTMLFormElement>) => {
    await handleSubmit(e);
    await refreshUploadedFiles();
    setPendingFiles([]);
  };

  const { onSourcesChange } = useExternalApiToggles({
    conversationIdRef,
    enabledSources,
    setEnabledSources,
  });

  // chat files handlers
  const refreshUploadedFiles = async () => {
    const id = conversationIdRef.current || conversationId;
    if (!id) return;
    try {
      const data = await conversationFilesService.listConversationFiles(
        Number(id)
      );
      setUploadedFiles(data.files);
    } catch {
      // optional: toast/snackbar
    }
  };

  const handleUploadFiles = async (files: FileList) => {
    const id = conversationIdRef.current || conversationId;
    if (!files?.length) return;

    for (const f of Array.from(files)) {
      try {
        setUploading(true);
        const created = await conversationFilesService.uploadConversationFile(
          Number(id ?? 0),
          f,
          true
        );

        console.log("Uploaded file:", created);
        if (created.conversation_id !== 0) {
          const convId = created.conversation_id
            ? String(created.conversation_id)
            : "";
          setConversationId(convId);
          conversationIdRef.current = convId;
          setSearchParams({ conversationId: convId });
        }

        setPendingFiles((prev) => [...prev, created]);
      } catch {
        toastService.error(`Failed to upload file: ${f.name}`);
      } finally {
        setUploading(false);
      }
    }
  };

  const handleDeletePendingFile = async (fileId: string) => {
    const id = conversationIdRef.current || conversationId;
    if (!id) return;
    try {
      setDeletingFileId(fileId);
      await conversationFilesService.deleteConversationFile(Number(id), fileId);
      setPendingFiles((prev) =>
        prev.filter((f) => String(f.id) !== String(fileId))
      );
    } catch {
      // optional toast
    } finally {
      setDeletingFileId(null);
    }
  };

  const handleDeleteUploadedFile = async (fileId: string) => {
    const id = conversationIdRef.current || conversationId;
    if (!id) return;
    try {
      await conversationFilesService.deleteConversationFile(Number(id), fileId);
      await refreshUploadedFiles();
    } catch {
      // optional: toast/snackbar
    }
  };

  useEffect(() => {
    const fetchSourceOptions = async () => {
      const data = await externalApiService.getAvailable();
      setSourceOptions(
        data.map(
          (item: any): SourceOption => ({
            id: item.name,
            label: item.display_name,
            description: item.description,
            enabled: false,
          })
        )
      );
    };

    fetchSourceOptions();
  }, []);

  useEffect(() => {
    refreshUploadedFiles();
  }, [conversationId]);

  return (
    <div className="px-8 flex-1 flex flex-col">
      <div className="flex-1 flex flex-col">
        <SubTitleHeader
          title={`Your Assistant for Project Insights`}
          subtitle="Ask me about your projects — select a project, file or report for deeper insights."
          spacer={!scopedDefined}
          subtitleStyle="text-gray-400 text-sm 2xl:text-base"
          childrenTitle={
            <>
              {scopedDefined && (
                <div className="flex items-center space-x-2">
                  <SourcesDropdown
                    options={sourceOptions}
                    value={enabledSources}
                    onChange={onSourcesChange}
                    ariaLabel="Knowledge sources"
                    classNameButton="w-10 h-10 border-[#E4E7EC!important]"
                    disabled={isChatLoading || sidebarDisabled}
                  />

                  {uploading ? (
                    <div className="w-10 h-10 flex items-center justify-center rounded-md border border-[#E4E7EC] bg-white cursor-not-allowed">
                      <Loader size="sm" color="#2162EB" />
                    </div>
                  ) : (
                    <label
                      htmlFor={headerUploadId}
                      className={`w-10 h-10 flex items-center justify-center rounded-md border border-[#E4E7EC] bg-white ${
                        isChatLoading || sidebarDisabled
                          ? "opacity-50 pointer-events-none"
                          : "cursor-pointer"
                      }`}
                      title="Attach files"
                      aria-label="Attach files"
                    >
                      <img src="/paperclip.svg" alt="" className="w-4 h-4" />
                    </label>
                  )}

                  <input
                    ref={headerUploadRef}
                    id={headerUploadId}
                    type="file"
                    multiple
                    className="sr-only"
                    onChange={(e) => {
                      if (e.target.files?.length) {
                        handleUploadFiles(e.target.files);
                        e.currentTarget.value = "";
                      }
                    }}
                  />

                  {selectedProject.id && (
                    <div className="flex space-x-1.5 h-10 items-center bg-[#FFFFFE] border border-[#E4E7EC] text-gray-900 font-medium p-2.5 rounded-md text-xs 2xl:text-sm focus:outline-none">
                      <img
                        src="/folder.svg"
                        className="2xl:w-6 2xl:h-6 h-5 w-5"
                        alt=""
                      />
                      <TruncatedNameWithTooltip
                        name={selectedProject.name}
                        widthClass="max-w-56"
                        textClass="text-xs 2xl:text-sm mt-1"
                        tooltip={true}
                        tooltipClass=""
                        tooltipPlacement="top"
                        arrow={false}
                      />
                    </div>
                  )}

                  {selectedReport.id && (
                    <div className="flex space-x-1.5 h-10 items-center bg-[#FFFFFE] border border-[#E4E7EC] text-gray-900 font-medium p-2.5 rounded-md text-xs 2xl:text-sm focus:outline-none">
                      <img
                        src="/file-text.svg"
                        className="2xl:w-6 2xl:h-6 h-5 w-5"
                        alt=""
                      />
                      <TruncatedNameWithTooltip
                        name={selectedReport.name}
                        widthClass="max-w-56"
                        textClass="text-xs 2xl:text-sm mt-1"
                        tooltipClass=""
                        tooltipPlacement="top"
                        arrow={false}
                      />
                    </div>
                  )}

                  {selectedFiles.length > 0 && (
                    <ReadOnlySelectedFilesView selected={selectedFiles} />
                  )}
                </div>
              )}
            </>
          }
          children={
            <NewItemButton
              onClick={() => {
                navigate("/assistant?new=1");
              }}
              title="Topic"
              disabled={isChatLoading || sidebarDisabled}
            />
          }
        />

        {loading ? (
          <div className="text-center py-10 flex-1 flex items-center justify-center">
            <Loader size="lg" color="#2162EB" />
          </div>
        ) : (
          <div className="flex flex-col justify-center 2xl:w-[75%] w-[85%] mx-auto p-4 h-[calc(100vh-152px)]">
            {/* Chat Messages */}
            {chatLog.length > 0 ? (
              <div
                className="flex-1 py-4 space-y-4 overflow-y-auto scrollbar-hide"
                id="chatlogDiv"
                ref={chatLogRef}
              >
                {chatLog.map((msg, index) => {
                  const hasInlineImageParts =
                    Array.isArray((msg as any).message_parts) &&
                    (msg as any).message_parts.some(
                      (p: any) => p?.type === "plots_data"
                    );

                  // NEW: compute per-message "new" *file* attachments (chips rendered ABOVE the bubble)
                  const newFileAttachments = getNewFileAttachmentsForMessage(
                    chatLog,
                    index
                  );

                  return (
                    <div
                      key={`chat-msg-${index}-${conversationId}`}
                      className="w-full"
                    >
                      {/* Show intermediate steps for bot */}
                      {msg.sender === "bot" && msg.intermediate_steps && (
                        <IntermediatePanel
                          item={{ content: msg.intermediate_steps }}
                        />
                      )}

                      {/* ---- NEW: attachments chips ABOVE the bubble (images only) ---- */}
                      {newFileAttachments.length > 0 && (
                        <div
                          className={`mb-2 ${
                            msg.sender === "user"
                              ? "flex justify-end"
                              : "flex justify-start"
                          }`}
                        >
                          <div className="flex flex-wrap gap-2 max-w-[100%] justify-end">
                            {newFileAttachments.map((file) => (
                              <span
                                key={`att-chip-${file.id}`}
                                className="inline-flex items-center gap-1.5 p-1.5 text-[11px] 2xl:text-xs rounded-md border border-[#E4E7EC] bg-white"
                                title={file.name}
                              >
                                <img
                                  src={getFileIcon(
                                    file.name.split(".").pop() || ""
                                  )}
                                  alt="image icon"
                                  className="w-6 h-6"
                                />
                                <span className="">{file.name}</span>
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      {/* ---- END: attachments chips ABOVE the bubble ---- */}

                      {/* Message bubble (unchanged layout, no attachments inside) */}
                      <div
                        className={`py-3 rounded-lg whitespace-pre-wrap break-words text-sm 2xl:text-base ${
                          msg.sender === "user"
                            ? "bg-blue-500 text-white ml-auto max-w-[75%] px-3"
                            : "text-gray-800 mr-auto pb-0"
                        } ${msg.message === "" ? "hidden" : ""}`}
                      >
                        {msg.message !== "" && (
                          <>
                            {conversationIdParam ? (
                              // OLD CHAT
                              <>
                                {hasInlineImageParts ? (
                                  <>
                                    {React.isValidElement(msg.message) ||
                                    Array.isArray(msg.message) ? (
                                      <>{msg.message}</>
                                    ) : typeof msg.message === "string" ? (
                                      <Markdown options={{ overrides }}>
                                        {msg.message}
                                      </Markdown>
                                    ) : (
                                      <div className="text-red-500">
                                        Invalid message format
                                      </div>
                                    )}
                                  </>
                                ) : (
                                  <>
                                    {Array.isArray(
                                      msg.generated_images_s3_keys
                                    ) &&
                                      msg.generated_images_s3_keys.map(
                                        (img: any, i: number) => (
                                          <img
                                            key={`chat_generated_${img.filename}`}
                                            src={convertS3UrlToHttps(img) || ""}
                                            alt={img?.filename}
                                            className="max-w-[65%] rounded cursor-pointer border border-gray-200 p-4 mb-6 hover:border-gray-300 transition-colors shadow"
                                            onClick={() => {
                                              showAndSetModalImage(
                                                convertS3UrlToHttps(img),
                                                img?.filename || `image-${i}`
                                              );
                                            }}
                                          />
                                        )
                                      )}

                                    {React.isValidElement(msg.message) ||
                                    Array.isArray(msg.message) ? (
                                      <>{msg.message}</>
                                    ) : typeof msg.message === "string" ? (
                                      <Markdown options={{ overrides }}>
                                        {msg.message}
                                      </Markdown>
                                    ) : (
                                      <div className="text-red-500">
                                        Invalid message format
                                      </div>
                                    )}
                                  </>
                                )}
                              </>
                            ) : (
                              // NEW CHAT
                              <>
                                {React.isValidElement(msg.message) ||
                                Array.isArray(msg.message) ? (
                                  <>{msg.message}</>
                                ) : typeof msg.message === "string" ? (
                                  <Markdown options={{ overrides }}>
                                    {msg.message}
                                  </Markdown>
                                ) : (
                                  <div className="text-red-500">
                                    Invalid message format
                                  </div>
                                )}
                              </>
                            )}
                          </>
                        )}

                        {/* Only show thumbs for bot messages */}
                        {!isChatLoading &&
                          msg.sender === "bot" &&
                          (index >= fetchedChatCount ||
                            !conversationIdParam) && (
                            <ThumbButtons
                              index={index}
                              selectedThumbs={selectedThumbs}
                              feedbackSubmittedFor={feedbackSubmittedFor}
                              handleThumbClick={handleThumbClick}
                              feedbackUpdating={feedbackUpdating}
                              alignment={"justify-end"}
                            />
                          )}
                      </div>

                      {!isChatLoading &&
                        feedbackForIndex === index &&
                        !feedbackHiddenFor[index] && (
                          <div className="ml-auto w-[75%]">
                            <FeedbackSubmission
                              index={index}
                              feedbackSubmittedFor={feedbackSubmittedFor}
                              feedbackState={feedbackState}
                              feedbackOption={feedbackOption}
                              feedbackText={feedbackText}
                              feedbackUpdating={feedbackUpdating}
                              setFeedbackText={setFeedbackText}
                              setFeedbackOption={setFeedbackOption}
                              setFeedbackState={setFeedbackState}
                              handleFeedbackSubmit={handleFeedbackSubmit}
                            />
                          </div>
                        )}
                    </div>
                  );
                })}

                {isChatLoading && showLoading && (
                  <div className="space-y-3">
                    {thinkingItem && <ThinkingPanel item={thinkingItem} />}
                    {stepItem && stepItem.type !== "intermediate_steps" && (
                      <StepsPanel item={stepItem} />
                    )}
                    {stepItem?.type === "intermediate_steps" && (
                      <IntermediatePanel item={stepItem} />
                    )}
                  </div>
                )}
              </div>
            ) : (
              <Header chatScope={chatScope} />
            )}

            {error && (
              <div className=" text-center py-4 max-w-96 mx-auto">
                <div className=" text-red-500 bg-red-100 py-2 px-4 border-red-500 border rounded-lg">
                  <p>{error}</p>
                </div>
                <button
                  onClick={tryAgain}
                  className="cursor-pointer my-4 text-sm bg-gray-100 text-blue-500 border-rounded px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Try Again
                </button>
              </div>
            )}

            <AssistantForm
              handleSubmit={handleSubmitWrapped}
              pendingFiles={pendingFiles}
              onDeletePendingFile={handleDeletePendingFile}
              isChatLoading={isChatLoading}
              question={question}
              setQuestion={setQuestion}
              selectedFiles={selectedFiles}
              setSelectedFiles={setSelectedFiles}
              chatScope={chatScope}
              setChatScope={setChatScope}
              selectedProject={selectedProject}
              setSelectedProject={setSelectedProject}
              selectedReport={selectedReport}
              setSelectedReport={setSelectedReport}
              scopedDefined={scopedDefined}
              enabledSources={enabledSources}
              setEnabledSources={setEnabledSources}
              sourceOptions={sourceOptions}
              onUploadFiles={handleUploadFiles}
              deletingFileId={deletingFileId}
              isFileUploading={uploading}
            />
          </div>
        )}
      </div>

      <ImageViewer
        showImageModal={showImageModal}
        setShowImageModal={setShowImageModal}
        modalImageSrc={modalImageSrc || ""}
        modalImageAlt={modalImageAlt || ""}
      />
    </div>
  );
};

export default Assistant;
